/**
 *  @class
 *  @function CollectionTabs (fixed + auto-sync on slide)
 *
 *  Key fixes:
 *   - Waits for Flickity instance to exist before operating
 *   - Hides slides instead of re-inserting/moving them (preserves DOM order)
 *   - Reloads Flickity and selects the first visible cell index
 *   - Updates active tab when user slides/swipes carousel
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._flkty = null;
      this._slider = null;
      this._waitTimeout = null;
      this._maxWaitMs = 3000; // safety: don't wait forever
    }

    connectedCallback() {
      // small delay to allow other scripts (like slideshow init) to run
      setTimeout(() => {
        const buttons = Array.from(this.querySelectorAll('button'));
        const sectionHeader = this.closest('.section-header');
        if (!sectionHeader) return; // ensure the header exists
        const links = sectionHeader.querySelectorAll('.linked-to-tab');

        this.target = this.dataset.target;
        this._slider = document.getElementById(this.target);
        if (!this._slider) return;

        // Attach click listeners to tab buttons
        buttons.forEach((button, i) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleButtons(buttons, i);
            this.toggleLinks(links, i);
            const handle = button.dataset.collection;
            if (handle) {
              this.showCollection(handle);
            }
          });
        });

        // Resize Flickity on window resize (if present)
        window.addEventListener('resize', () => {
          if (!this._slider) return;
          const flk = Flickity.data(this._slider);
          if (flk) flk.resize();
        });

        // --- NEW: Sync active tab with Flickity on slide change ---
        const waitForFlickity = () => {
          const flk = Flickity.data(this._slider);
          if (flk) {
            this._flkty = flk;

            // Listen to Flickity's select event
            flk.on('select', () => {
              const selectedSlide = flk.selectedElement;
              if (!selectedSlide) return;
              const activeHandle = selectedSlide.dataset.collection;
              if (!activeHandle) return;

              // Find and activate the matching tab
              const activeIndex = buttons.findIndex(
                (btn) => btn.dataset.collection === activeHandle
              );
              this.toggleButtons(buttons, activeIndex);
              this.toggleLinks(links, activeIndex);
            });
          } else {
            // Retry until Flickity exists
            setTimeout(waitForFlickity, 200);
          }
        };
        waitForFlickity();
        // --- END new Flickity sync code ---

        // Shopify editor behavior: select block -> trigger tab
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const blockIndex = Array.from(this.children).indexOf(event.target);
            if (blockIndex !== -1 && buttons[blockIndex]) {
              buttons[blockIndex].dispatchEvent(new Event('click'));
            }
          });
        }
      }, 20);
    }

    toggleButtons(buttons, index) {
      buttons.forEach((button) => button.classList.remove('active'));
      if (buttons[index]) buttons[index].classList.add('active');
    }

    toggleLinks(links, index) {
      if (!links || !links.length) return;
      links.forEach((link) => link.classList.remove('active'));
      if (links[index]) links[index].classList.add('active');
    }

    // Public: show a collection by handle (robust, preserves DOM)
    showCollection(handle) {
      if (!this._slider) return;

      // Wait for Flickity instance to exist (if it hasn't been created yet)
      this._ensureFlickityReady()
        .then(() => {
          this._applyCollectionFilter(handle);
        })
        .catch(() => {
          // fallback: still attempt to apply filter without flkty
          this._applyCollectionFilter(handle);
        });
    }

    // Wait for Flickity.data(slider) to be available (returns Promise)
    _ensureFlickityReady() {
      return new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
          const flk = Flickity.data(this._slider);
          if (flk) {
            this._flkty = flk;
            return resolve(flk);
          }
          if (Date.now() - start > this._maxWaitMs) {
            return reject(new Error('Flickity instance not found'));
          }
          this._waitTimeout = setTimeout(check, 60);
        };
        check();
      });
    }

    // Hide non-matching slides and update Flickity
    _applyCollectionFilter(handle) {
      const flkty = this._flkty || Flickity.data(this._slider);
      const slides = Array.from(this._slider.querySelectorAll('.carousel__slide'));

      // Fallback selector in case markup changes
      if (!slides.length) {
        slides.push(...Array.from(this._slider.children));
      }

      // Show only slides belonging to selected collection
      slides.forEach((slide) => {
        const collection = slide.dataset.collection;
        if (collection === handle) {
          slide.style.display = '';
          slide.hidden = false;
        } else {
          slide.style.display = 'none';
          slide.hidden = true;
        }
      });

      // If Flickity is active, reload and select first visible slide
      if (flkty) {
        flkty.reloadCells();

        const visibleCellIndex = (flkty.cells || [])
          .map((cell, idx) => ({ cell, idx }))
          .find(({ cell }) => {
            const el = cell && cell.element;
            return el && el.offsetParent !== null && el.style.display !== 'none';
          });

        if (visibleCellIndex) {
          flkty.select(visibleCellIndex.idx, false, true);
        } else {
          flkty.select(0, false, true);
        }
      } else {
        // Fallback: scroll to first visible slide
        const firstVisible = slides.find((s) => s.dataset.collection === handle);
        if (firstVisible)
          firstVisible.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}
